"use strict";
/**
 * 品牌图形生成（多语言之外的另一条"客户没有设计资源"的兜底）。
 *
 * AI 出图走服务端任务（图像模型要 Key、要计费、要审核），一条任务表、一份额度，
 * 但**出口不止一个**：
 *   · `full`   —— 模型连公司名一起画，出来就是一张成品标。好看，但字可能写错
 *   · `symbol` —— 模型只画图形，名字由前端用确定字体排上去，错字为零
 *   · `cover`  —— 照片类配图（分类卡封面、区块里的首屏图/横幅/配图）。
 *                保背景、不含字，画布比例由 `aspect` 决定
 *
 * 前两个是"标"，第三个是"图"。塞进同一张表是因为它们花的是同一笔钱、走的是
 * 同一套额度与配置 —— 分成两个模块只会让"这个月还剩几张"出现两个口径。
 * 但它们的**提示词、画布比例、后处理**完全不同，所以模式必须一路传到 provider。
 *
 * 一开始只做了 `symbol`，理由是"生成模型写不对公司名，尤其中文"。那个判断
 * 本身没错，但把它当成**唯一**选项是错的：模型这几代的字形能力涨得很快，
 * 而小客户手里连一个能看的标都没有 —— 给他一张只有图形的东西，他还得自己
 * 找字体、对间距。所以改成让客户自己选，默认给好看的，把"绝不会错字"那条
 * 路留在旁边当退路。
 *
 * 所有模式都走同一张任务表和同一份额度，客户不用理解差别，只要看预览。
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_BRAND_ART_BASE_URL = exports.DEFAULT_BRAND_ART_MODEL = exports.BRAND_ART_PRESETS = exports.BRAND_ART_MODE_KEYS = exports.BRAND_ART_RENDER_MODES = exports.BRAND_ART_MODES = exports.ART_ASPECTS = exports.ART_ASPECT_KEYS = exports.COVER_SUBJECT_LIMIT = exports.COVER_ART_STYLE_KEYS = exports.COVER_ART_STYLES = exports.BRAND_ART_STYLE_KEYS = exports.BRAND_ART_STYLES = void 0;
exports.isBrandArtStyle = isBrandArtStyle;
exports.stylePrompt = stylePrompt;
exports.styleLockup = styleLockup;
exports.styleIsRich = styleIsRich;
exports.styleLabel = styleLabel;
exports.isCoverArtStyle = isCoverArtStyle;
exports.coverStylePrompt = coverStylePrompt;
exports.isArtAspect = isArtAspect;
exports.artAspectSpec = artAspectSpec;
exports.artAspectShape = artAspectShape;
exports.isBrandArtMode = isBrandArtMode;
exports.artModeLabel = artModeLabel;
exports.colorWord = colorWord;
exports.brandColorHint = brandColorHint;
/**
 * 五条风格。
 *
 * ## 每一条都必须是"意象 + 配色规则"，不能只写风格词
 *
 * 上一版这里写的是 `a minimal geometric brand mark`、`monochrome` 这类**风格词**。
 * 模型收到"极简几何、单色"只能吐出一个剪影 —— 客户的原话是「都是单色的一个几何图形，
 * 很难看」。问题不在模型，在这句话没告诉它画什么。
 *
 * 所以现在每一条都干三件事：
 *   1. **给具体意象**（卡尺、齿轮、叶片、信号波）—— 抽象几何是"没有意象"的结果，不是一种风格
 *   2. **锁两色**（一个深主色 + 一个亮强调色）—— 一色太素，三色以上缩到 16px 会糊成一团
 *   3. **不提 monochrome** —— 那条词是单色的直接来源
 *
 * 具体色相由 `brandColorHint()` 在运行时按客户品牌色补，不写死在这里：
 * 同一个风格配墨绿和配藏蓝，出来的应该是两张不同的图。
 *
 * ## 每条风格有**两句话**，因为两个模式的"画面"不是一回事
 *
 * `prompt` 描述的是**一个孤立图形**（symbol 模式：我们要它缩小后仍是干净的剪影）。
 * `lockup` 描述的是**一枚成品标里的图形**（full 模式：旁边还要压一行公司名，
 * 模型有空间画细节，也不该只给一个剪影）。
 *
 * 上一版两个模式共用 `prompt`，整标就得到"一个孤零零的几何块 + 一行小字"——
 * 客户拿豆包同款引擎生成的对比图几乎是碾压：那边是刀具 + 齿轮 + 线路 + 光泽，
 * 这边是一块蓝。差的不只是渲染，是我们没给它画面。
 */
exports.BRAND_ART_STYLES = [
    {
        key: 'geometric',
        label: '几何',
        hint: '圆与弧的组合，最不容易过时',
        prompt: 'a bold geometric brand symbol built from circles, arcs and one intersecting plane, symmetric and balanced, two flat colors: a deep primary shape with one vivid contrast accent',
        lockup: 'a bold emblem of two concentric rings sliced by one diagonal plane, with a small solid core at the centre',
    },
    {
        key: 'minimal',
        label: '极简',
        hint: '一笔曲线加一块实心，适合服务型',
        prompt: 'a minimal brand symbol made of one confident sweeping curve and one solid shape, generous negative space, two flat colors: a deep primary shape with one vivid contrast accent',
        lockup: 'one confident sweeping curve wrapping a single solid geometric block, generous negative space',
    },
    {
        key: 'industrial',
        label: '工业',
        hint: '硬边实心块，适合制造',
        prompt: 'a bold industrial brand symbol suggesting precision calipers, a gear or a milling cutter profile, hard edges and sturdy solid blocks, two flat colors: a deep primary shape with one vivid contrast accent',
        lockup: 'a precision milling cutter seen in profile, flanked by the jaws of a caliper and a thin gear ring, drawn as sturdy machined shapes',
    },
    {
        key: 'tech',
        label: '科技',
        hint: '节点、信号、芯片感',
        prompt: 'a modern tech brand symbol suggesting connected nodes, a signal wave or a circuit path, crisp geometric edges, two flat colors: a deep primary shape with one vivid contrast accent',
        lockup: 'a drill bit silhouette fused with a circuit trace and a signal arc, fine bright accent lines running through a compact emblem',
    },
    {
        key: 'organic',
        label: '自然',
        hint: '柔和曲线，适合食品与生活',
        prompt: 'an organic brand symbol suggesting a leaf, a water drop or flowing grain, soft rounded shapes, two flat colors: a deep primary shape with one vivid contrast accent',
        lockup: 'a leaf and a water drop overlapping into one rounded emblem, soft flowing contour with one crisp edge',
    },
];
exports.BRAND_ART_STYLE_KEYS = exports.BRAND_ART_STYLES.map((item) => item.key);
function isBrandArtStyle(value) {
    return typeof value === 'string' && exports.BRAND_ART_STYLE_KEYS.includes(value);
}
function stylePrompt(style) {
    return exports.BRAND_ART_STYLES.find((item) => item.key === style)?.prompt ?? exports.BRAND_ART_STYLES[0].prompt;
}
/**
 * 成品标里那个图形该画什么。
 *
 * 和 `stylePrompt()` 分开是因为整标有空间画细节：客户要的是"能放在页头上的成品标"，
 * 不是"一个可以缩到 16px 的图标"。两件事的画面密度差一个量级。
 */
function styleLockup(style) {
    return (exports.BRAND_ART_STYLES.find((item) => item.key === style)?.lockup ?? exports.BRAND_ART_STYLES[0].lockup);
}
/**
 * 这条风格允不允许"质感"。
 *
 * 科技/工业要金属、渐变、微光 —— 客户嘴里那个"科技感"就是这些。而几何/极简/自然
 * 本来就该是平的：给一片叶子加高光只会显得廉价。
 *
 * **只对整标放开**：整标在页头里是 200px 以上的大图，渐变看得见；而符号模式要缩到
 * 16px 当 favicon，渐变在那里只是一团糊。
 */
function styleIsRich(style) {
    return style === 'tech' || style === 'industrial';
}
function styleLabel(style) {
    return exports.BRAND_ART_STYLES.find((item) => item.key === style)?.label ?? style;
}
/* ── 封面（分类卡配图） ─────────────────────────────────────────────── */
/**
 * 四套封面画法。
 *
 * ## 为什么和标的五条风格是两套
 *
 * 标的风格描述的是"符号的视觉语言"（几何、工业、自然），客户挑的是标的气质。
 * 封面的客户挑的是**画面**：是拍车间、拍产品、拍材质，还是干脆只留光影。
 * 把标那五条搬过来会得到一句"用工业语言画一个封面"—— 模型只能给你一张图形，
 * 而分类卡要的是一张能铺满 3:2 的图。
 *
 * ## 为什么每一条都要点明"没有字、没有 logo"
 *
 * 封面下面紧接着就是分类名（前台渲染出来的 h3）。模型很爱自己往角上加一行
 * 编号或水印式的字，那行字会和真标题打架，而且错字没法改 —— 只能重生成。
 *
 * ## 为什么 `scene` 排第一
 *
 * 分类卡的作用是"让人一眼知道这一类是什么"。实拍场景在这件事上比产品特写稳：
 * 产品特写挑错型号就等于说错话，场景只说"这里在做什么"。
 */
exports.COVER_ART_STYLES = [
    {
        key: 'scene',
        label: '实拍场景',
        hint: '车间、仓库、施工现场，最有氛围',
        prompt: 'a natural professional wide shot of the real place where this work happens — the workshop, warehouse or site, with believable depth, soft daylight and honest traces of work (tools, materials, machines) — no one posing for the camera',
    },
    {
        key: 'product',
        label: '产品特写',
        hint: '一两件成品占满画面，适合卖货的站',
        prompt: 'one or two finished products of this kind, shot as a clean commercial product photograph on a simple seamless background, soft studio light, crisp focus, generous breathing space around the subject',
    },
    {
        key: 'macro',
        label: '微距细节',
        hint: '材质与工艺的特写，质感最强',
        prompt: 'an extreme close-up of the material and the craftsmanship — surface texture, machined edges, grain or weave filling the frame, raking light that makes the texture readable',
    },
    {
        key: 'abstract',
        label: '抽象质感',
        hint: '不画具体东西，只留光影与形',
        prompt: 'an abstract composition built from light, shadow and a few simple geometric forms echoing this business, calm and premium, no recognisable objects',
    },
];
exports.COVER_ART_STYLE_KEYS = exports.COVER_ART_STYLES.map((item) => item.key);
function isCoverArtStyle(value) {
    return typeof value === 'string' && exports.COVER_ART_STYLE_KEYS.includes(value);
}
function coverStylePrompt(style) {
    return exports.COVER_ART_STYLES.find((item) => item.key === style)?.prompt ?? exports.COVER_ART_STYLES[0].prompt;
}
/**
 * 「画什么」那一栏的长度上限。
 *
 * 80 个字符足够写一句「不锈钢管件与法兰堆在托盘上」，再长模型也不会更准 ——
 * 它会把超出的部分当成风格要求，反而把主体画散。
 */
exports.COVER_SUBJECT_LIMIT = 80;
/**
 * 比例档的**真源**。
 *
 * 类型从这份清单推出来，而不是反过来（`key: ArtAspect` 里再引 `typeof ART_ASPECTS`）：
 * 那样写是一个循环引用，TS 解不开就退化成 `any`，而 `any` 在这一层的代价是
 * 后端拿到一个拼错的比例也不报错，只是图裁歪了。
 */
exports.ART_ASPECT_KEYS = [
    'cover',
    'landscape',
    'square',
    'wide',
    'pano',
    'card',
];
exports.ART_ASPECTS = [
    {
        key: 'cover',
        label: '3:2 分类卡',
        width: 1620,
        height: 1080,
        ark: '1536x1024',
        shape: '3:2 landscape',
    },
    {
        key: 'landscape',
        label: '4:3 横图',
        width: 1600,
        height: 1200,
        ark: '1536x1152',
        shape: '4:3 landscape',
    },
    {
        key: 'square',
        label: '1:1 方图',
        width: 1200,
        height: 1200,
        ark: '1536x1536',
        shape: 'square',
    },
    {
        key: 'wide',
        label: '21:9 宽幅',
        width: 2100,
        height: 900,
        ark: '2048x880',
        shape: '21:9 ultra-wide landscape',
    },
    {
        key: 'pano',
        label: '3:1 通栏',
        width: 1800,
        height: 600,
        ark: '2160x720',
        shape: '3:1 panoramic landscape',
    },
    {
        key: 'card',
        label: '16:10 卡片',
        width: 1600,
        height: 1000,
        ark: '1536x960',
        shape: '16:10 landscape',
    },
];
function isArtAspect(value) {
    return typeof value === 'string' && exports.ART_ASPECT_KEYS.includes(value);
}
/**
 * 比例档 → 规格。认不出的值一律退回 `cover`（3:2）。
 *
 * 兜底而不是抛错：比例只影响"裁成什么形状"，为它让整次生成失败不值得。
 * 退回 3:2 至少是一张能用在任何位置的横图。
 */
function artAspectSpec(aspect) {
    return exports.ART_ASPECTS.find((item) => item.key === aspect) ?? exports.ART_ASPECTS[0];
}
/** 写进提示词的那句构图描述 */
function artAspectShape(aspect) {
    return artAspectSpec(aspect).shape;
}
/* ── 渲染模式 ───────────────────────────────────────────────────────── */
/**
 * 标面板上的两种画法。
 *
 * `full` 排在前面，是因为它才是客户心里的"AI 生成一个 logo"。
 * `symbol` 不是次一等的选项，而是**退路**：字写错了、名字是中文、
 * 想让标和站上的文字用同一种字体 —— 这三种情况都该用它。
 *
 * `cover` 不在这里：它是"配图"，跟"标"不是一个出口，摆进这个列表
 * 只会让客户在标面板上看到一条他不敢点的选项。
 */
exports.BRAND_ART_MODES = [
    {
        key: 'full',
        label: '图形 + 文字',
        hint: '模型连公司名一起画，像成品标。字可能写错，看预览验收',
    },
    {
        key: 'symbol',
        label: '只画图形',
        hint: '模型只画那个图形，名字由我们用确定字体排上去 —— 绝不会错字',
    },
];
/**
 * 落库的渲染模式全集（`brand_art_jobs.render_mode`）。
 *
 * 校验用它，不用 `BRAND_ART_MODES` —— 后者只是标面板的两个选项。
 */
exports.BRAND_ART_RENDER_MODES = ['full', 'symbol', 'cover'];
exports.BRAND_ART_MODE_KEYS = exports.BRAND_ART_RENDER_MODES;
function isBrandArtMode(value) {
    return typeof value === 'string' && exports.BRAND_ART_MODE_KEYS.includes(value);
}
function artModeLabel(mode) {
    // cover 现在是"配图"这一类（分类卡 + 区块里的图），叫"分类封面"会让人以为
    // 它只能用在分类卡上
    if (mode === 'cover')
        return '配图';
    return exports.BRAND_ART_MODES.find((item) => item.key === mode)?.label ?? mode;
}
/* ── 颜色 → 文字 ─────────────────────────────────────────────────────── */
/**
 * 把客户品牌色翻成一个**模型看得懂的颜色名**。
 *
 * ## 为什么给颜色名，不给十六进制
 *
 * Seedream / GPT Image 这类模型对 `#1f5f4b` 这种串基本没有稳定的响应，
 * 但对 `deep forest green` 有。约束的是"深绿"这个区间，不是那一个色值 ——
 * logo 上也确实不需要精确到 hex：差几个色号看不出来，跑偏成另一个色系才难看。
 *
 * ## 为什么要带明度前缀
 *
 * 双色方案里两块的**明度必须拉开**，否则缩到 16px 是两块连在一起的灰。
 * `deep` / `vivid` / `soft` 这几个前缀就是让模型自己去拉这个差。
 */
function colorWord(input) {
    const hex = (input ?? '').trim().replace(/^#/, '');
    const full = hex.length === 3 ? `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}` : hex;
    if (!/^[0-9a-f]{6}$/i.test(full))
        return null;
    const r = parseInt(full.slice(0, 2), 16) / 255;
    const g = parseInt(full.slice(2, 4), 16) / 255;
    const b = parseInt(full.slice(4, 6), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    const s = d === 0 ? 0 : d / (1 - Math.abs(2 * l - 1));
    // 极暗的色不论有没有色相，模型只会画成黑。先归类再谈色相 ——
    // `#101828` 这种带一点蓝的近黑，按饱和度算会被判成蓝色，那就成了瞎指挥
    if (l < 0.13)
        return 'near-black';
    // 无彩色单独处理：`deep gray` 比 `deep slate` 更不容易被模型理解成蓝色
    if (s < 0.12) {
        if (l < 0.45)
            return 'charcoal gray';
        if (l < 0.75)
            return 'mid gray';
        return 'light gray';
    }
    let h = 0;
    if (max === r)
        h = ((g - b) / d) % 6;
    else if (max === g)
        h = (b - r) / d + 2;
    else
        h = (r - g) / d + 4;
    h = (h * 60 + 360) % 360;
    const hue = h < 14 || h >= 345
        ? 'red'
        : h < 38
            ? 'orange'
            : h < 62
                ? 'amber'
                : h < 95
                    ? 'lime'
                    : h < 150
                        ? 'green'
                        : h < 185
                            ? 'teal'
                            : h < 200
                                ? 'cyan'
                                : h < 250
                                    ? 'blue'
                                    : h < 275
                                        ? 'indigo'
                                        : h < 300
                                            ? 'violet'
                                            : h < 330
                                                ? 'magenta'
                                                : 'pink';
    if (l < 0.3)
        return `deep ${hue}`;
    if (l < 0.62)
        return s >= 0.45 ? `vivid ${hue}` : `muted ${hue}`;
    if (l < 0.82)
        return `soft ${hue}`;
    return `pale ${hue}`;
}
/**
 * 双色的颜色指令。
 *
 * 第二块用**同一个色相的另一档明度**，不用第三个独立色相：客户只给了我们一个品牌色，
 * 凭空再发明一个色相，出来的标和他自己的站就对不上了。
 *
 * 方向按主色的明暗定 —— 深品牌色往亮里走、浅品牌色往暗里走，这样两块的明度
 * 永远拉得开。都往亮里提的话，浅色品牌（比如米黄）会得到"米黄 + 更浅的米黄"，
 * 缩到 16px 就是一块。
 */
function brandColorHint(primary) {
    const deep = colorWord(primary);
    if (!deep)
        return null;
    const base = parseHex(primary);
    if (!base)
        return `Color palette: use ${deep} as the dominant shape`;
    // 相对亮度 > 0.42 视为浅色，往暗里走；否则往亮里走
    const step = lightnessOf(base) > 0.42 ? -0.22 : 0.2;
    const other = colorWord(shiftLightness(base, step));
    return other && other !== deep
        ? `Color palette: ${deep} as the dominant shape, ${other} as the single accent`
        : `Color palette: use ${deep} as the dominant shape`;
}
function parseHex(input) {
    const hex = (input ?? '').trim().replace(/^#/, '');
    const full = hex.length === 3 ? `${hex[0]}${hex[0]}${hex[1]}${hex[1]}${hex[2]}${hex[2]}` : hex;
    if (!/^[0-9a-f]{6}$/i.test(full))
        return null;
    return [
        parseInt(full.slice(0, 2), 16),
        parseInt(full.slice(2, 4), 16),
        parseInt(full.slice(4, 6), 16),
    ];
}
/** HSL 里的 L，0–1。判断明暗用它而不是 RGB 均值：蓝和黄的均值一样但明暗差很远 */
function lightnessOf(rgb) {
    return (Math.max(...rgb) + Math.min(...rgb)) / 510;
}
/** 只动明度，色相和饱和度不动 —— 换色相会让强调色跑到另一个色系去 */
function shiftLightness(rgb, amount) {
    const [r, g, b] = rgb.map((value) => value / 255);
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    const l = (max + min) / 2;
    const d = max - min;
    let h = 0;
    let s = 0;
    if (d !== 0) {
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        if (max === r)
            h = ((g - b) / d) % 6;
        else if (max === g)
            h = (b - r) / d + 2;
        else
            h = (r - g) / d + 4;
        h *= 60;
        if (h < 0)
            h += 360;
    }
    const nl = Math.min(1, Math.max(0, l + amount));
    const c = (1 - Math.abs(2 * nl - 1)) * s;
    const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
    const m = nl - c / 2;
    const segment = Math.floor(h / 60) % 6;
    const rgbOut = segment === 0
        ? [c, x, 0]
        : segment === 1
            ? [x, c, 0]
            : segment === 2
                ? [0, c, x]
                : segment === 3
                    ? [0, x, c]
                    : segment === 4
                        ? [x, 0, c]
                        : [c, 0, x];
    return `#${rgbOut
        .map((value) => Math.round((value + m) * 255)
        .toString(16)
        .padStart(2, '0'))
        .join('')}`;
}
exports.BRAND_ART_PRESETS = [
    {
        key: 'openai-image-2',
        label: 'OpenAI GPT Image 2',
        baseUrl: 'https://api.openai.com/v1',
        model: 'gpt-image-2',
        price: '约 ¥0.38/张（medium）',
        note: '最听得懂 no text，还能直接出透明底；需跨境结算',
    },
    {
        key: 'openai-image-15',
        label: 'OpenAI GPT Image 1.5',
        baseUrl: 'https://api.openai.com/v1',
        model: 'gpt-image-1.5',
        price: '约 ¥0.25/张（medium）',
        note: '便宜一档，图形细节略简',
    },
    {
        key: 'ark-seedream-5-flash',
        label: '火山方舟 Seedream 5.0 Flash',
        baseUrl: 'https://ark.cn-beijing.volces.com/api/v3',
        // 方舟的 Model ID 带日期后缀，裸名 `doubao-seedream-5-0-flash` 会 model not found
        model: 'doubao-seedream-5-0-flash-260915',
        price: '¥0.12/张',
        note: '国内发票、出图快；接口默认加水印，我们已经替它关掉',
    },
    {
        key: 'ark-seedream-4',
        label: '火山方舟 Seedream 4.0',
        baseUrl: 'https://ark.cn-beijing.volces.com/api/v3',
        model: 'doubao-seedream-4-0-250828',
        price: '¥0.20/张',
        note: '稳定、便宜，做图形够用',
    },
];
/** 默认模型。别写死 gpt-image-1 —— 它已在 2026-10-23 下线 */
exports.DEFAULT_BRAND_ART_MODEL = 'gpt-image-2';
exports.DEFAULT_BRAND_ART_BASE_URL = 'https://api.openai.com/v1';
//# sourceMappingURL=brand.js.map